import { useState } from 'react';
import { ExternalLink, LocateFixed, MapPin } from 'lucide-react';

import { cn } from '../../utils/cn';
import { getCurrentPosition, parseCoordinates } from '../../utils/geolocation';
import { Button } from '../ui';

// GPS readings worse than this are filled in but flagged for a check on the map.
const LOW_ACCURACY_M = 100;

interface CampusLocationFieldProps {
    coordinates: string;
    radius: string;
    coordinatesError?: string;
    radiusError?: string;
    onCoordinatesChange: (value: string) => void;
    onRadiusChange: (value: string) => void;
}

// Check-in location of a campus: coordinates (pasted or from this device's GPS) and the allowed radius.
export default function CampusLocationField({
    coordinates,
    radius,
    coordinatesError,
    radiusError,
    onCoordinatesChange,
    onRadiusChange,
}: CampusLocationFieldProps) {
    const [locating, setLocating] = useState(false);
    const [gpsNote, setGpsNote] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
    const parsed = parseCoordinates(coordinates);

    const useCurrentLocation = async () => {
        setLocating(true);
        setGpsNote(null);
        try {
            const position = await getCurrentPosition();
            onCoordinatesChange(`${position.latitude.toFixed(7)}, ${position.longitude.toFixed(7)}`);
            setGpsNote(position.accuracyM > LOW_ACCURACY_M
                ? { tone: 'warn', text: `Đã lấy vị trí nhưng sai số lớn (±${position.accuracyM}m). Hãy kiểm tra trên bản đồ hoặc thử lại ngoài trời.` }
                : { tone: 'ok', text: `Đã lấy vị trí hiện tại (sai số ±${position.accuracyM}m).` });
        } catch (error: any) {
            setGpsNote({ tone: 'error', text: error?.message || 'Không lấy được vị trí.' });
        } finally {
            setLocating(false);
        }
    };

    const inputClass = (error?: string) => cn(
        'block min-h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2',
        error ? 'border-danger-100 focus:border-danger-600 focus:ring-danger-100' : 'border-ink-200 focus:border-academy-500 focus:ring-academy-100',
    );

    return (
        <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
                <div className="space-y-1.5">
                    <label htmlFor="campus-coordinates" className="block text-sm font-medium text-ink-700">Tọa độ</label>
                    <div className="flex gap-2">
                        <input
                            id="campus-coordinates"
                            value={coordinates}
                            onChange={event => onCoordinatesChange(event.target.value)}
                            placeholder="10.7769, 106.7009"
                            inputMode="decimal"
                            className={cn(inputClass(coordinatesError), 'min-w-0 flex-1 tabular-nums')}
                        />
                        <Button
                            type="button"
                            variant="secondary"
                            loading={locating}
                            icon={<LocateFixed className="h-4 w-4" />}
                            onClick={useCurrentLocation}
                            className="shrink-0"
                            title="Lấy tọa độ từ GPS của thiết bị này"
                        >
                            <span className="hidden sm:inline">Vị trí hiện tại</span>
                            <span className="sm:hidden">Vị trí</span>
                        </Button>
                    </div>
                </div>
                <div className="space-y-1.5">
                    <label htmlFor="campus-radius" className="block text-sm font-medium text-ink-700">Bán kính</label>
                    <div className="relative">
                        <input
                            id="campus-radius"
                            type="number"
                            min={20}
                            max={5000}
                            value={radius}
                            onChange={event => onRadiusChange(event.target.value)}
                            className={cn(inputClass(radiusError), 'pr-9 tabular-nums')}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-400">m</span>
                    </div>
                </div>
            </div>

            {coordinatesError || radiusError ? (
                <p className="text-sm text-danger-600">{coordinatesError || radiusError}</p>
            ) : null}
            {gpsNote ? (
                <p className={cn('text-sm', gpsNote.tone === 'ok' ? 'text-success-700' : gpsNote.tone === 'warn' ? 'text-warning-700' : 'text-danger-600')}>
                    {gpsNote.text}
                </p>
            ) : null}

            {parsed ? (
                <a
                    href={`https://www.google.com/maps?q=${parsed.latitude},${parsed.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-academy-700 hover:text-academy-800"
                >
                    <MapPin className="h-4 w-4" />
                    Xem trên bản đồ
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            ) : (
                <p className="text-xs text-ink-500">
                    Đang ở cơ sở thì bấm "Vị trí hiện tại". Hoặc trên Google Maps, nhấn chuột phải vào cơ sở, bấm vào dòng tọa độ để sao chép rồi dán vào đây.
                    Chưa có tọa độ thì mọi lần điểm danh ở cơ sở này đều được ghi là không xác định vị trí.
                </p>
            )}
        </div>
    );
}
